import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { createBrowserHistory } from 'history';
import { createStore, applyMiddleware, Store } from 'redux';
import thunk from 'redux-thunk';
import { ConnectedRouter, routerMiddleware } from 'connected-react-router';
import { Provider } from 'react-redux';

import createRootReducer, { RootState } from './reducers/index';
import {
    I18nProvider,
    migrateLocaleStorage,
    isSupportedLocale,
    DEFAULT_LOCALE,
} from './i18n';
import { installApiErrorInterceptor, installSessionExpiredInterceptor, installAcceptLanguageInterceptor } from './utils/apiError';
import { LOCALE_STORAGE_KEY } from './i18n/config';
import * as constants from './constants/constants';

import 'bootstrap/dist/css/bootstrap.css';
import { ToastContainer } from 'react-toastify';
import { renderToastIcon, mikaToastTransition } from './components/Toast/MikaToast';
import 'react-toastify/dist/ReactToastify.css';
import 'react-image-lightbox/style.css';
import 'leaflet/dist/leaflet.css';

import App from './App';
import reportWebVitals from './reportWebVitals';

// Run the legacy localStorage migration before anything else reads locale state.
// Maps old 'mikaSelectedLanguage' shapes (numeric {value:1|2} and raw 'al'/'en')
// into the new 'mika.locale' BCP 47 code.
const initialLocale = migrateLocaleStorage();

// Structured error envelope ({error:{code,params}}) — tolerant of legacy {message}.
installApiErrorInterceptor(axios);

// Forward the user's current UI locale on every API request so the Laravel
// SetLocale middleware can resolve app()->setLocale() for server-generated
// output (PDFs, emails). Reads from localStorage so the redux store isn't needed.
installAcceptLanguageInterceptor(axios, () => {
    try {
        const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
        return isSupportedLocale(stored) ? stored : DEFAULT_LOCALE;
    } catch {
        return DEFAULT_LOCALE;
    }
});

// Catches "my token just got revoked mid-session" (happens when a super-admin
// suspends/inactivates this tenant's company). Clears local auth state and
// bounces to /login. The toast is shown ON THE LOGIN PAGE (via ?reason=
// session_ended) instead of here — a pre-redirect toast competes with every
// component's own 401-catch toast (many `catch { toast.error('Diqka nuk shkoi
// mire') }` sites), and the redirect wipes them before the user can read anything.
installSessionExpiredInterceptor(axios, () => {
    try {
        localStorage.removeItem('mikaBearerToken');
        localStorage.removeItem('username');
        localStorage.removeItem('mikaUserId');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('mikaNavbarToggled');
    } catch { /* ignore */ }

    if (window.location.pathname !== '/login') {
        // Hard navigation — wipes stale React state + all in-flight toasts.
        window.location.href = '/login?reason=session_ended';
    }
});

const history = createBrowserHistory();
const middleware = applyMiddleware(thunk, routerMiddleware(history));

export const store = createStore(
    createRootReducer(history) as any,
    undefined,
    middleware as any,
) as Store<RootState>;

// Seed locale from the (migrated) localStorage value so I18nProvider picks it up on first render.
store.dispatch({
    type: constants.SET_LOCALE,
    payload: isSupportedLocale(initialLocale) ? initialLocale : DEFAULT_LOCALE,
});

ReactDOM.render(
    <Provider store={store}>
        <I18nProvider modules={['common', 'auth', 'contact', 'measure', 'nav', 'offer', 'agreement', 'dataTransfer', 'production', 'warehouse', 'transport', 'installation', 'service', 'admin', 'analytics', 'integrations', 'notifications']}>
            <ConnectedRouter history={history}>
                <ToastContainer
                    position="bottom-center"
                    autoClose={4000}
                    hideProgressBar
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    icon={renderToastIcon}
                    transition={mikaToastTransition}
                    theme="light"
                    className="mika-toaster"
                />
                <App history={history} />
            </ConnectedRouter>
        </I18nProvider>
    </Provider>,
    document.getElementById('root'),
);

reportWebVitals();
export {};
